import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Box, Typography, CircularProgress, Button, Alert, Stack, TextField } from "@mui/material";
import { useAuthStore } from "@/store/auth-store";

/**
 * Mobile-side counterpart to the desktop "Pair another device" screen.
 *
 * Flow
 * ----
 * 1. Desktop user (already signed in) calls /api/pair/issue → gets a one-time
 *    code, displays it as a QR pointing at `https://whb-client.pages.dev/pair?code=XYZ`.
 * 2. Mobile user scans the QR with their phone camera. The phone's default
 *    camera + URL handler opens this page in their browser.
 * 3. This page reads `?code=XYZ` from the URL and POSTs it to
 *    /api/pair/redeem. The server reads the JWT it stored in KV, sets the
 *    `whb_jwt` HttpOnly cookie on the mobile device's domain, and 204s.
 * 4. Page redirects to `/` — the user lands on the dashboard fully signed in.
 *
 * Industry pattern: this is the WhatsApp Web / Discord / Signal Desktop
 * "scan QR from another device" flow, adapted for a PWA.
 *
 * Fallback
 * --------
 * If the user types `/pair` directly with no code, we render a TextField
 * for them to enter the code manually (the desktop screen also displays
 * the code as text alongside the QR for this case).
 */
export function PairPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const signInReal = useAuthStore((s) => s.signInReal);
  const codeFromUrl = searchParams.get("code") ?? "";
  const [status, setStatus] = useState<"idle" | "redeeming" | "ok" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  const [manualCode, setManualCode] = useState("");

  // Auto-redeem on first load when ?code=… is in the URL.
  useEffect(() => {
    if (!codeFromUrl) return;
    void redeem(codeFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function redeem(code: string) {
    setStatus("redeeming");
    setError(null);
    try {
      const res = await fetch("/api/pair/redeem", {
        method: "POST",
        headers: { "content-type": "application/json" },
        // Include credentials so the Set-Cookie response is honored even
        // when the browser prefers to "wait for an interaction" — Chrome
        // mobile is occasionally cautious about Set-Cookie on first-load
        // requests, but credentials: include + same-origin clears that.
        credentials: "include",
        body: JSON.stringify({ code: code.trim() }),
      });
      if (res.ok) {
        // Server set the HttpOnly cookie. JS can't read it, so we also
        // need to flip the auth store into real-auth mode — otherwise
        // the auth gate at "/" sees signedIn === false and bounces
        // straight back to /sign-in (Sergey 2026-04-25 mobile screenshot).
        // Mirrors what /auth/callback does after /api/session.
        let exp: number = Math.floor(Date.now() / 1000) + 60 * 60;
        try {
          const body = (await res.json()) as { exp?: number };
          if (typeof body.exp === "number" && Number.isFinite(body.exp)) {
            exp = body.exp;
          }
        } catch {
          /* tolerate non-JSON / older server */
        }
        signInReal(null, exp);
        setStatus("ok");
        // Force a full reload so anything reading the auth state on
        // mount (data hooks, refresh schedulers, …) re-initialises
        // cleanly. Same pattern as /auth/callback's `replace("/")`.
        setTimeout(() => {
          window.location.replace("/");
        }, 600);
        return;
      }
      // Non-204 — surface a friendly message.
      const txt = await res.text().catch(() => "");
      let friendly = `Pairing failed (${res.status}).`;
      if (res.status === 410) friendly = "This code has expired or already been used.";
      else if (res.status === 400) friendly = "That code doesn't look right. Try again.";
      else if (res.status === 503) friendly = "Pairing is not configured on the server yet.";
      setError(friendly + (txt ? ` (${txt})` : ""));
      setStatus("error");
    } catch {
      setError("Couldn't reach the pairing service. Check your connection and try again.");
      setStatus("error");
    }
  }

  // No code in URL → manual-entry view.
  if (!codeFromUrl && status === "idle") {
    return (
      <Box sx={{ p: 3, maxWidth: 460, mx: "auto" }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Pair this device
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          On your signed-in computer, open <strong>Settings → Pair another device</strong>.
          Type the code shown there:
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            placeholder="Pairing code"
            value={manualCode}
            onChange={(e) => setManualCode(e.target.value)}
            fullWidth
            autoFocus
            // Mobile keyboard hint: alphanumeric, no auto-correct.
            inputProps={{ autoCapitalize: "off", autoCorrect: "off", spellCheck: false }}
          />
          <Button
            variant="contained"
            disabled={!manualCode.trim()}
            onClick={() => redeem(manualCode)}
          >
            Pair
          </Button>
        </Stack>
        <Button onClick={() => navigate("/")} sx={{ mt: 2 }} size="small">
          Back to app
        </Button>
      </Box>
    );
  }

  // In-flight states.
  return (
    <Box sx={{ p: 3, maxWidth: 460, mx: "auto", textAlign: "center" }}>
      {status === "redeeming" && (
        <>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="body1">Pairing this device…</Typography>
        </>
      )}
      {status === "ok" && (
        <Alert severity="success">Paired! Redirecting…</Alert>
      )}
      {status === "error" && (
        <>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error ?? "Pairing failed."}
          </Alert>
          <Stack spacing={1}>
            <Button variant="outlined" onClick={() => navigate("/pair")}>
              Try a different code
            </Button>
            <Button onClick={() => navigate("/")} size="small">
              Back to app
            </Button>
          </Stack>
        </>
      )}
    </Box>
  );
}
